import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Package, PencilSimple, Trash, MagnifyingGlass, CurrencyDollar,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

function formatPrice(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const emptyForm = { name: "", description: "", price: "", is_active: true };

export default function Products() {
  const { currentOrg } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Products isn't enabled for this workspace");
      } else {
        toast.error("Could not load products");
      }
      setProducts([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return products;
    const q = query.toLowerCase();
    return products.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [products, query]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p.product_id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      price: p.price === null || p.price === undefined ? "" : String(p.price),
      is_active: p.is_active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    let priceValue = null;
    if (form.price !== "" && form.price !== null && form.price !== undefined) {
      const n = Number(form.price);
      if (Number.isNaN(n)) { toast.error("Price must be a number"); return; }
      if (n < 0) { toast.error("Price cannot be negative"); return; }
      priceValue = n;
    }
    const payload = {
      name,
      description: form.description,
      price: priceValue,
      is_active: !!form.is_active,
    };
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/products/${editing}`, payload);
        setProducts((all) => all.map((x) => x.product_id === editing ? data : x));
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/products", payload);
        setProducts((all) => [data, ...all]);
        toast.success("Product added");
      }
      setDialogOpen(false); setForm(emptyForm); setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/products/${confirmDelete.product_id}`);
      setProducts((all) => all.filter((x) => x.product_id !== confirmDelete.product_id));
      toast.success(`Removed ${confirmDelete.name}`);
    } catch { toast.error("Could not remove"); }
    finally { setConfirmDelete(null); }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="products-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Products</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            The catalogue of services and offerings this workspace sells. Deactivate ones you no longer offer instead of deleting them.
          </p>
        </div>
        <button
          data-testid="products-add"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 transition-colors"
        >
          <Plus weight="bold" className="h-4 w-4" />
          Add product
        </button>
      </div>

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
        <input
          data-testid="products-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by product name"
          className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30"
        />
      </div>

      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading products…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="products-empty">
            <Package weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/50" />
            <div className="mt-4 font-display text-xl tracking-tight">
              {products.length === 0 ? "No products yet" : "No matches"}
            </div>
            <p className="mt-2 text-sm text-ink-secondary max-w-sm mx-auto">
              {products.length === 0
                ? "Add the services and offerings this workspace sells."
                : "Try a different search term."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="products-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Description</th>
                  <th className="px-5 py-3 font-medium text-right">Price</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Added</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((p) => (
                  <tr key={p.product_id} data-testid={`product-row-${p.product_id}`} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-3">
                      <div className="text-ink-primary" data-testid={`product-name-${p.product_id}`}>{p.name}</div>
                    </td>
                    <td className="px-5 py-3 text-ink-secondary max-w-md">
                      <div className="line-clamp-2 text-xs">{p.description || "—"}</div>
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-ink-primary" data-testid={`product-price-${p.product_id}`}>
                      {p.price === null || p.price === undefined ? (
                        <span className="text-ink-secondary">—</span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5">
                          <CurrencyDollar weight="bold" className="h-3 w-3 opacity-60" />
                          {formatPrice(p.price)}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        data-testid={`product-status-${p.product_id}`}
                        className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${p.is_active ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-200" : "border-white/10 text-ink-secondary"}`}
                      >
                        {p.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">{formatDate(p.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`product-edit-${p.product_id}`}
                          onClick={() => openEdit(p)}
                          className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                          title="Edit"
                        >
                          <PencilSimple weight="bold" className="h-3.5 w-3.5" />
                        </button>
                        <button
                          data-testid={`product-delete-${p.product_id}`}
                          onClick={() => setConfirmDelete(p)}
                          className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"
                          title="Remove"
                        >
                          <Trash weight="bold" className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm(emptyForm); setEditing(null); } } }}>
        <DialogContent data-testid="product-dialog" className="max-w-2xl bg-graphite-900 border-white/10 text-ink-primary">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">
              {editing ? "Edit product" : "Add product"}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              An offering this workspace sells. Later releases will link products into campaigns, quotes and invoices.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
              <Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={200} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Price <span className="opacity-60">(optional)</span></label>
              <Input
                data-testid="product-form-price"
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="0.00"
                className="bg-graphite-950 border-white/10 text-ink-primary"
              />
            </div>
            <div className="space-y-1.5 flex flex-col justify-end">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Status</label>
              <div className="flex items-center gap-3 h-10">
                <Switch
                  data-testid="product-form-active"
                  checked={!!form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
                <span className={`text-xs ${form.is_active ? "text-emerald-300/90" : "text-ink-secondary"}`}>
                  {form.is_active ? "Active — currently offered" : "Inactive — hidden from new work"}
                </span>
              </div>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Description</label>
              <Textarea data-testid="product-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="bg-graphite-950 border-white/10 text-ink-primary" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button
              data-testid="product-form-cancel"
              onClick={() => { if (!saving) { setDialogOpen(false); setForm(emptyForm); setEditing(null); } }}
              disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              data-testid="product-form-save"
              onClick={save}
              disabled={saving || !form.name.trim()}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Add product"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="product-delete-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove product?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              {confirmDelete?.name} will be soft-deleted. Data is retained but it'll no longer appear in this list. To just stop offering it, deactivate instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="product-delete-confirm" onClick={doDelete} className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
